import { useEffect, useState } from "react";
import { endOfDay, format, isBefore, startOfDay } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DateRange } from "react-day-picker";
import { Button } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PERIOD_PRESETS, isPeriodPresetActive, type PeriodPreset } from "./period-presets";

interface Props {
  /** Substitui a descrição do Explorador quando a barra é reusada em outra aba. */
  description?: string;
  from: string;
  onCustomRangeChange: (from: Date, to: Date) => void;
  onPeriodChange: (period: PeriodPreset) => void;
  period: PeriodPreset;
  /** Substitui o eyebrow "Explorador" quando a barra é reusada em outra aba. */
  title?: string;
  to: string;
}

/**
 * Mesma estrutura e mesmas funções das outras heroes de período de Relatórios
 * (AppointmentsPeriodHero, RevenuePeriodHero, StockPeriodHero): eyebrow +
 * assunto à esquerda, data grande abaixo, pílulas de atalho + calendário de
 * intervalo arbitrário à direita — inclusive o próprio calendário de seleção
 * dia a dia, não só os presets.
 *
 * Única diferença deliberada: os presets aqui são os seis já usados no resto
 * do explorador (hoje/semana/mês/3/6/12 meses via period-presets.ts), não os
 * quatro fixos (hoje/7d/30d/mês) das outras heroes — esse vocabulário de
 * período já é o que o resto do explorador usa, e reduzir para quatro
 * tiraria opções que a spec do explorador pede (3/6/12 meses).
 */
export function SalesExplorerPeriodBar({ description, from, onCustomRangeChange, onPeriodChange, period, title, to }: Props) {
  const { i18n, t } = useTranslation("reports");
  const locale = i18n.language === "pt" ? ptBR : enUS;

  const dateFrom = new Date(from);
  const dateTo = new Date(to);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>({ from: dateFrom, to: dateTo });
  const [selectionStep, setSelectionStep] = useState<"from" | "to">("from");

  useEffect(() => {
    setRange({ from: dateFrom, to: dateTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    if (calendarOpen) setSelectionStep("from");
  }, [calendarOpen]);

  const periodLabel = `${format(dateFrom, "dd MMM yyyy", { locale })} – ${format(dateTo, "dd MMM yyyy", { locale })}`;

  function handleDayClick(day: Date) {
    const clicked = startOfDay(day);

    if (selectionStep === "from") {
      setRange({ from: clicked, to: undefined });
      setSelectionStep("to");
      return;
    }

    const rangeStart = range?.from ? startOfDay(range.from) : clicked;
    const nextFrom = isBefore(clicked, rangeStart) ? clicked : rangeStart;
    const nextTo = isBefore(clicked, rangeStart) ? rangeStart : clicked;
    setRange({ from: nextFrom, to: nextTo });
    onCustomRangeChange(nextFrom, endOfDay(nextTo));
    setSelectionStep("from");
    setCalendarOpen(false);
  }

  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-card p-6 md:flex-row md:items-start md:justify-between">
      {/* Esquerda — assunto */}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            {title ?? t("salesExplorerTabLabel")}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{description ?? t("salesExplorerDesc")}</p>
        <h2 className="truncate text-2xl font-bold tracking-tight">{periodLabel}</h2>
      </div>

      {/* Direita — controles */}
      <div className="flex shrink-0 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {PERIOD_PRESETS.map((preset) => {
            const active = isPeriodPresetActive(from, to, preset.value);
            return (
              <Button
                className={cn("h-7 rounded-full px-3 text-xs")}
                key={preset.value}
                onClick={() => onPeriodChange(preset.value)}
                size="sm"
                variant={active ? "default" : "outline"}
              >
                {t(preset.labelKey)}
              </Button>
            );
          })}
        </div>

        <Popover onOpenChange={setCalendarOpen} open={calendarOpen}>
          <PopoverTrigger asChild>
            <Button className="h-8 justify-start gap-2 text-xs" size="sm" variant="outline">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
              {/* Mesma variável do título grande (periodLabel), não o
                  formatPeriodRange condensado — no StockPeriodHero o botão e
                  o H2 mostram exatamente o mesmo texto. */}
              <span className="max-w-[220px] truncate">{periodLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0">
            <Calendar locale={locale} mode="range" numberOfMonths={2} onDayClick={handleDayClick} selected={range} />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
